import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = ({ currentPage, setCurrentPage }) => {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isDocumentsDropdownOpen, setIsDocumentsDropdownOpen] = useState(false);
  const documentsDropdownCloseTimer = useRef(null);

  // Блокируем скролл страницы, когда мобильное меню открыто
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    return () => {
      if (documentsDropdownCloseTimer.current) {
        clearTimeout(documentsDropdownCloseTimer.current);
      }
    };
  }, []);

  const openDocumentsDropdown = () => {
    if (documentsDropdownCloseTimer.current) {
      clearTimeout(documentsDropdownCloseTimer.current);
    }
    setIsDocumentsDropdownOpen(true);
  };

  const closeDocumentsDropdown = () => {
    documentsDropdownCloseTimer.current = setTimeout(() => {
      setIsDocumentsDropdownOpen(false);
    }, 180);
  };

  const documentGroups = {
    group1: {
      title: t("nav_group_procedures"),
      items: [
        { key: "reglament", label: t("nav_reglament") },
        { key: "sborahi", label: t("nav_sborahi") },
        { key: "reestr", label: t("nav_reestr") },
        { key: "porpod", label: t("nav_porpod") },
        { key: "porrasarb", label: t("nav_porrasarb") },
        { key: "poroznom", label: t("nav_poroznom") },
        { key: "china_cooperation", label: t("nav_china_cooperation") },
      ],
    },
    group2: {
      title: t("nav_group_samples"),
      items: [
        { key: "schema", label: t("nav_schema") },
        { key: "sogl", label: t("nav_sogl") },
        { key: "obrazcy", label: t("nav_obrazcy") },
        { key: "pogashenie", label: t("nav_pogashenie") },
        { key: "isk_edin_arbitr", label: t("nav_isk_edin_arbitr") },
        { key: "otzyv_isk", label: t("nav_otzyv_isk") },
        { key: "arbitrazh_sogl", label: t("nav_arbitrazh_sogl") },
        { key: "raspiski", label: t("nav_raspiski") },
        { key: "universal_ogovorka", label: t("nav_universal_ogovorka") },
      ],
    },
  };

  const navItems = [
    { key: "home", label: t("nav_main") },
    { key: "about", label: t("nav_about") },
    {
      key: "documents",
      label: t("nav_documents"),
      isDropdown: true,
      dropdownGroups: documentGroups,
    },
    { key: "news", label: t("nav_news") },
    { key: "contact", label: t("nav_contacts") },
  ];

  const languages = [
    { code: "ru", label: "Русский" },
    { code: "en", label: "English" },
    { code: "kz", label: "Қазақша" },
    { code: "zh", label: "中文" },
  ];

  const allDropdownItems = [
    ...documentGroups.group1.items,
    ...documentGroups.group2.items,
  ];

  return (
    <>
      <header className="fixed top-0 left-0 w-full bg-white shadow-lg z-50 border-b-4 border-blue-900">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-2">
            {/* Logo Section */}
            <button
              onClick={() => setCurrentPage("home")}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-24 h-24 flex items-center justify-center">
                <img
                  src="/images/logo-blue2.png"
                  alt={t("logo_alt")}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-blue-900 font-bold text-lg leading-tight max-w-xs hidden md:block">
                {t("logo_text")}
              </div>
            </button>

            <div className="flex-1 mx-4 text-center">
              {/* <p className="text-blue-800 italic font-medium text-xs sm:text-sm tracking-wide leading-snug max-w-xs mx-auto">
                {t("hero_slogan")}
              </p> */}

              {/* mobile: просто номера на белом фоне */}
              <div className="flex flex-col items-center mt-2 gap-1 md:hidden">
                <a
                  href="tel:+77073293969"
                  className="text-blue-800 text-sm font-semibold"
                >
                  +7 (707) 329-39-69
                </a>
                <a
                  href="tel:+77015588033"
                  className="text-blue-800 text-sm font-semibold"
                >
                  +7 (701) 558-80-33
                </a>
              </div>

              {/* desktop: синие кнопки как раньше */}
              <div className="hidden md:flex justify-center gap-2 mt-2">
                <a
                  href="tel:+77073293969"
                  className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 rounded-md text-xs sm:text-sm font-semibold tracking-wide hover:bg-blue-700 transition"
                >
                  +7 (707) 329-39-69
                </a>

                <a
                  href="tel:+77015588033"
                  className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 rounded-md text-xs sm:text-sm font-semibold tracking-wide hover:bg-blue-700 transition"
                >
                  +7 (701) 558-80-33
                </a>
              </div>
            </div>

            {/* --- ВОТ ЭТОТ БЛОК БЫЛ ПУСТЫМ, ТЕПЕРЬ ОН ВОССТАНОВЛЕН --- */}
            <nav className="hidden lg:flex items-center space-x-8 mr-6">
              {navItems.map((item) => (
                <div key={item.key} className="relative">
                  {item.isDropdown ? (
                    <div
                      className="relative"
                      onMouseEnter={openDocumentsDropdown}
                      onMouseLeave={closeDocumentsDropdown}
                    >
                      <button className="flex items-center space-x-1 text-blue-700 hover:text-blue-900 hover:border-b-2 hover:border-blue-900 pb-1 transition-all font-medium">
                        <span>{item.label}</span>
                        <ChevronDown className="w-4 h-4" />
                      </button>

                      {isDocumentsDropdownOpen && (
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-10 w-max">
                          <div className="bg-white shadow-xl rounded-lg border p-4">
                            <div className="flex space-x-8">
                              {Object.values(documentGroups).map((group) => (
                                <div key={group.title} className="flex-1">
                                  <h3 className="font-bold text-blue-900 px-4 pb-2 mb-2 border-b">
                                    {group.title}
                                  </h3>
                                  <div className="flex flex-col">
                                    {group.items.map((subItem) => (
                                      <button
                                        key={subItem.key}
                                        onClick={() => {
                                          setCurrentPage(subItem.key);
                                          setIsDocumentsDropdownOpen(false);
                                        }}
                                        className="block w-full text-left px-4 py-2 text-blue-700 hover:bg-gray-50 hover:text-blue-900 transition-colors rounded-md"
                                      >
                                        {subItem.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setCurrentPage(item.key)}
                      className={`text-blue-700 hover:text-blue-900 hover:border-b-2 hover:border-blue-900 pb-1 transition-all font-medium ${
                        currentPage === item.key
                          ? "border-b-2 border-blue-900 text-blue-900"
                          : ""
                      }`}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              ))}
            </nav>

            {/* Language Switcher & Mobile Menu Button */}
            <div className="flex items-center space-x-4">
              {/* --- И ЭТОТ БЛОК ДЛЯ ДЕСКТОПА ТОЖЕ ВОССТАНОВЛЕН --- */}
              <div className="relative hidden lg:block">
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="px-3 py-1 border border-gray-300 rounded text-blue-900 font-bold hover:bg-gray-50 transition-colors"
                >
                  {currentLanguage.toUpperCase()}
                </button>

                {isLangMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-white shadow-xl rounded-lg border py-1 min-w-32 z-20">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setIsLangMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-blue-700 hover:bg-gray-50 hover:text-blue-900 transition-colors"
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-blue-900"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay & Panel */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>

        <div
          className={`relative bg-white transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          <div className="container mx-auto px-4 pt-32 pb-8 h-screen overflow-y-auto">
            <nav className="flex flex-col space-y-3">
              {navItems.map((item) => (
                <div key={item.key}>
                  {item.isDropdown ? (
                    <div>
                      <button
                        onClick={() =>
                          setIsDocumentsDropdownOpen(!isDocumentsDropdownOpen)
                        }
                        className="flex items-center justify-between w-full p-3 text-lg text-gray-700 hover:bg-gray-100 rounded-lg"
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          className={`w-5 h-5 transition-transform ${
                            isDocumentsDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isDocumentsDropdownOpen && (
                        <div className="ml-4 mt-1 space-y-1 border-l-2 pl-4">
                          {allDropdownItems.map((subItem) => (
                            <button
                              key={subItem.key}
                              onClick={() => {
                                setCurrentPage(subItem.key);
                                setIsMobileMenuOpen(false);
                              }}
                              className="block w-full text-left p-3 text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                              {subItem.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setCurrentPage(item.key);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`block w-full text-left p-3 text-lg rounded-lg transition-colors ${
                        currentPage === item.key
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              ))}

              <div className="pt-4 mt-4 border-t">
                <div className="px-3 mb-2 text-sm font-medium text-gray-500">
                  {t("language_selector_title")}
                </div>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left p-3 rounded-lg transition-colors ${
                      currentLanguage === lang.code
                        ? "bg-blue-100 text-blue-900 font-semibold"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
